import React, {useState, useEffect, useLayoutEffect, useRef} from 'react'
import {createPortal} from 'react-dom'
import {StatusBadge} from '../StatusBadge'
import {
  TableWrapper,
  StyledTable,
  THead,
  TBody,
  TR,
  TH,
  THContent,
  SortArrows,
  TD,
  BusinessNameText,
  OptionsCell,
  OptionsLabel,
  OptionsTooltip,
  OptionsTooltipItem,
  StatusCell,
  StatusTooltip,
  EmptyState,
  EmptyStateTitle,
  EmptyStateBtn,
  LoadingSpinner,
  FeedbackWrapper,
  FeedbackIconBtn,
  FeedbackPopover,
  FeedbackPopoverText,
  ActionsRow,
  ActionBtn,
  DeleteBtn,
  Spinner,
  ExpiringDateCell,
  ExpiringDateText,
  ExpiringTooltip,
} from './styled.components'
import {
  PTCaretDown,
  PTCaretUp,
  PTComment,
  PTDeleteSVG,
  PTDuplicate,
  PTEdit,
  PTView,
} from '../../../../assets'

const SORT_KEY_MAP = {
  businessName: 'businessName',
  email: 'clientEmail',
  createdBy: 'salesRepId',
  created: 'createdAt',
  lastModified: 'updatedAt',
  expiration: 'expirationDate',
  status: 'statusCode',
}

const COLUMNS = [
  {key: 'businessName', label: 'Business Name', sortable: true},
  {key: 'proposalOptions', label: 'Proposal Options', sortable: false},
  {key: 'email', label: 'Email', sortable: true},
  {key: 'createdBy', label: 'Created By User', sortable: true, optional: true},
  {key: 'created', label: 'Created', sortable: true},
  {key: 'lastModified', label: 'Last Modified', sortable: true},
  {key: 'status', label: 'Status', sortable: true},
  {key: 'expiration', label: 'Expiration Date', sortable: true},
  {key: 'actions', label: 'Actions', sortable: false},
]

const LOCKED_STATUSES = ['Accepted', 'Completed']

function isExpiringWithin10Days(expirationStr) {
  if (!expirationStr) return false
  const expDate = new Date(expirationStr)
  if (isNaN(expDate)) return false
  const now = new Date()
  const diffMs = expDate - now
  const diffDays = diffMs / (1000 * 60 * 60 * 24)
  return diffDays >= 0 && diffDays <= 10
}

const STATUS_DESCRIPTIONS = {
  Draft: 'Saved but not yet sent to the client.',
  Sent: 'Delivered to the client by email or text.',
  Viewed: 'Client viewed the proposal.',
  Completed: 'Client paid and the order is finalized.',
  Declined: 'Client requested a change to the proposal.',
  Expired: 'No longer valid. Create a new proposal to proceed.',
}

// Gap between a trigger and its floating tooltip/popover (arrow included).
const FLOATING_GAP = 8
const FEEDBACK_GAP = 12

// All three tooltip types render through a portal to document.body as
// position: fixed, so their coordinates come from the trigger's actual
// getBoundingClientRect() rather than a parent-relative CSS offset (see the
// TableWrapper comment in styled.components.js for why they can't just be
// normal descendants of the table).
function floatingPositionStyle(anchorRect, openDown, gap) {
  const left = anchorRect.left + anchorRect.width / 2
  return openDown
    ? {left, top: anchorRect.bottom + gap}
    : {left, bottom: window.innerHeight - anchorRect.top + gap}
}

function renderFloating(floating, ref) {
  const style = floatingPositionStyle(
    floating.anchorRect,
    floating.openDown,
    floating.gap,
  )
  if (floating.kind === 'feedback') {
    return (
      <FeedbackPopover
        ref={ref}
        style={style}
        openDown={floating.openDown}
        onMouseDown={e => e.stopPropagation()}
      >
        <FeedbackPopoverText>
          &ldquo;{floating.content}&rdquo;
        </FeedbackPopoverText>
      </FeedbackPopover>
    )
  }
  if (floating.kind === 'status') {
    return (
      <StatusTooltip
        ref={ref}
        style={style}
        openDown={floating.openDown}
        role="tooltip"
      >
        {floating.content}
      </StatusTooltip>
    )
  }
  return (
    <OptionsTooltip ref={ref} style={style} openDown={floating.openDown}>
      {floating.content}
    </OptionsTooltip>
  )
}

function ProposalTable({
  rows,
  loading,
  sort,
  onSort,
  onNewProposal,
  onDuplicate,
  onPreview,
  onEdit,
  onDelete,
  previewLoadingId,
  editLoadingId,
  readOnly = false,
  showCreatedBy = false,
  createdByNames = {},
}) {
  const [floating, setFloating] = useState(null)
  const floatingRef = useRef(null)
  const columns = showCreatedBy ? COLUMNS : COLUMNS.filter(c => !c.optional)

  // Close the feedback popover on any outside click (hover-driven tooltips
  // already close on mouseleave/blur, so they don't need this).
  useEffect(() => {
    if (!floating || floating.kind !== 'feedback') return
    const close = () => setFloating(null)
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [floating])

  // Every floating box is positioned from a one-time getBoundingClientRect()
  // snapshot taken when it opens, so it doesn't track the trigger if the
  // page (or the horizontally-scrolling TableWrapper) scrolls afterward.
  // Simplest fix: dismiss it on any scroll, the same way clicking outside
  // already dismisses it, rather than leaving it drifted off its anchor.
  useEffect(() => {
    if (!floating) return
    const close = () => setFloating(null)
    window.addEventListener('scroll', close, {capture: true, passive: true})
    return () => window.removeEventListener('scroll', close, {capture: true})
  }, [floating])

  // Every floating box first renders "open up" (openDown: false). Once
  // mounted — but before the browser paints, since this is a layout effect —
  // check whether that placement actually pokes above the viewport, and flip
  // it if so. This is the same measure-then-correct approach Floating UI
  // uses, and it's what lets these tooltips work regardless of how many rows
  // are in the table or where TableWrapper's own edges land.
  useLayoutEffect(() => {
    if (!floating || floating.openDown) return
    const el = floatingRef.current
    if (!el) return
    if (el.getBoundingClientRect().top < 0) {
      setFloating(prev => (prev ? {...prev, openDown: true} : prev))
    }
  }, [floating])

  const showFloating = (e, id, kind, content, gap) => {
    const anchorRect = e.currentTarget.getBoundingClientRect()
    setFloating({id, kind, content, anchorRect, gap, openDown: false})
  }

  const hideFloating = id => {
    setFloating(prev => (prev && prev.id === id ? null : prev))
  }

  const handleFeedbackClick = (e, rowId, content) => {
    e.stopPropagation()
    const id = `feedback-${rowId}`
    const anchorRect = e.currentTarget.getBoundingClientRect()
    setFloating(prev =>
      prev && prev.id === id
        ? null
        : {
            id,
            kind: 'feedback',
            content,
            anchorRect,
            gap: FEEDBACK_GAP,
            openDown: false,
          },
    )
  }

  const activeSortApiKey = sort ? sort.column : null
  const activeSortDir = sort ? sort.direction : null

  if (loading) {
    return (
      <EmptyState>
        <LoadingSpinner />
        <EmptyStateTitle style={{marginTop: '16px'}}>
          Loading proposals…
        </EmptyStateTitle>
      </EmptyState>
    )
  }

  if (rows.length === 0) {
    return (
      <EmptyState>
        <EmptyStateTitle>No proposals found</EmptyStateTitle>
        {!readOnly && (
          <EmptyStateBtn type="button" onClick={onNewProposal}>
            + Create New Proposal
          </EmptyStateBtn>
        )}
      </EmptyState>
    )
  }

  return (
    <>
      <TableWrapper>
        <StyledTable>
          <THead>
            <TR>
              {columns.map(col => {
                const apiKey = SORT_KEY_MAP[col.key]
                const isActive = col.sortable && apiKey === activeSortApiKey
                return (
                  <TH
                    key={col.key}
                    sortable={col.sortable}
                    onClick={
                      col.sortable
                        ? () => onSort && onSort(SORT_KEY_MAP[col.key])
                        : undefined
                    }
                  >
                    <THContent>
                      {col.label}
                      {col.sortable && (
                        <SortArrows>
                          <img
                            src={PTCaretUp}
                            alt=""
                            width="6"
                            style={{
                              opacity:
                                isActive && activeSortDir === 'asc' ? 1 : 0.3,
                            }}
                          />
                          <img
                            src={PTCaretDown}
                            alt=""
                            width="6"
                            style={{
                              opacity:
                                isActive && activeSortDir === 'desc' ? 1 : 0.3,
                            }}
                          />
                        </SortArrows>
                      )}
                    </THContent>
                  </TH>
                )
              })}
            </TR>
          </THead>

          <TBody>
            {rows.map(row => {
              const isLocked = LOCKED_STATUSES.includes(row.status)
              const statusDescription = STATUS_DESCRIPTIONS[row.status]
              const expiringSoon = isExpiringWithin10Days(row.expiration)
              const optionsId = `options-${row.id}`
              const createdByTooltipId = `createdby-${row.id}`
              const statusTooltipId = `status-${row.id}`
              return (
                <TR key={row.id}>
                  <TD>
                    <BusinessNameText>{row.businessName}</BusinessNameText>
                  </TD>

                  <TD>
                    <OptionsCell>
                      <OptionsLabel
                        onMouseEnter={
                          row.options.length > 0
                            ? e =>
                                showFloating(
                                  e,
                                  optionsId,
                                  'options',
                                  row.options.map((name, i) => (
                                    <OptionsTooltipItem key={i}>
                                      {name}
                                    </OptionsTooltipItem>
                                  )),
                                  FLOATING_GAP,
                                )
                            : undefined
                        }
                        onMouseLeave={() => hideFloating(optionsId)}
                      >
                        {row.options.length} options
                      </OptionsLabel>
                      {row.feedback && (
                        <FeedbackWrapper>
                          <FeedbackIconBtn
                            type="button"
                            title="View feedback"
                            onMouseDown={e =>
                              handleFeedbackClick(e, row.id, row.feedback)
                            }
                          >
                            <img src={PTComment} alt="Feedback" width="14" />
                          </FeedbackIconBtn>
                        </FeedbackWrapper>
                      )}
                    </OptionsCell>
                  </TD>

                  <TD>{row.email}</TD>

                  {showCreatedBy && (
                    <TD>
                      {createdByNames[row.createdBy] ? (
                        <StatusCell
                          tabIndex={0}
                          onMouseEnter={e =>
                            showFloating(
                              e,
                              createdByTooltipId,
                              'status',
                              row.createdBy,
                              FLOATING_GAP,
                            )
                          }
                          onMouseLeave={() => hideFloating(createdByTooltipId)}
                          onFocus={e =>
                            showFloating(
                              e,
                              createdByTooltipId,
                              'status',
                              row.createdBy,
                              FLOATING_GAP,
                            )
                          }
                          onBlur={() => hideFloating(createdByTooltipId)}
                        >
                          {createdByNames[row.createdBy]}
                        </StatusCell>
                      ) : (
                        row.createdBy
                      )}
                    </TD>
                  )}

                  <TD>{row.created}</TD>
                  <TD>{row.lastModified}</TD>

                  <TD>
                    <StatusCell
                      tabIndex={statusDescription ? 0 : undefined}
                      onMouseEnter={
                        statusDescription
                          ? e =>
                              showFloating(
                                e,
                                statusTooltipId,
                                'status',
                                statusDescription,
                                FLOATING_GAP,
                              )
                          : undefined
                      }
                      onMouseLeave={() => hideFloating(statusTooltipId)}
                      onFocus={
                        statusDescription
                          ? e =>
                              showFloating(
                                e,
                                statusTooltipId,
                                'status',
                                statusDescription,
                                FLOATING_GAP,
                              )
                          : undefined
                      }
                      onBlur={() => hideFloating(statusTooltipId)}
                    >
                      <StatusBadge status={row.status} />
                    </StatusCell>
                  </TD>
                  <TD>
                    {expiringSoon ? (
                      <ExpiringDateCell>
                        <span>⚠</span>
                        <ExpiringDateText>{row.expiration}</ExpiringDateText>
                        <ExpiringTooltip>Expires soon</ExpiringTooltip>
                      </ExpiringDateCell>
                    ) : (
                      row.expiration || ''
                    )}
                  </TD>

                  <TD>
                    <ActionsRow>
                      {!readOnly && (
                        <ActionBtn
                          type="button"
                          title="Duplicate"
                          onClick={() => onDuplicate && onDuplicate(row)}
                        >
                          <img src={PTDuplicate} alt="Duplicate" width="15" />
                        </ActionBtn>
                      )}

                      <ActionBtn
                        type="button"
                        title="Preview"
                        disabled={previewLoadingId === row.id}
                        onClick={() => onPreview && onPreview(row)}
                      >
                        {previewLoadingId === row.id ? (
                          <Spinner />
                        ) : (
                          <img src={PTView} alt="Preview" width="15" />
                        )}
                      </ActionBtn>

                      {!readOnly && (
                        <ActionBtn
                          type="button"
                          title="Edit"
                          disabled={isLocked || editLoadingId === row.id}
                          onClick={
                            !isLocked ? () => onEdit && onEdit(row) : undefined
                          }
                        >
                          {editLoadingId === row.id ? (
                            <Spinner />
                          ) : (
                            <img src={PTEdit} alt="Edit" width="15" />
                          )}
                        </ActionBtn>
                      )}

                      {!readOnly && (
                        <DeleteBtn
                          type="button"
                          title="Delete"
                          disabled={isLocked}
                          onClick={
                            !isLocked
                              ? () => onDelete && onDelete(row)
                              : undefined
                          }
                        >
                          <PTDeleteSVG width="14" height="14" />
                        </DeleteBtn>
                      )}
                    </ActionsRow>
                  </TD>
                </TR>
              )
            })}
          </TBody>
        </StyledTable>
      </TableWrapper>

      {floating &&
        createPortal(renderFloating(floating, floatingRef), document.body)}
    </>
  )
}

export {ProposalTable}
